import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";

import { Spinner } from "../components/ui/page-spinner";
import { isAuthenticatedAppPath } from "../marketing/content/routes";
import { MarketingRouteTree } from "./marketingRoutes";

const AuthenticatedAppRouter = lazy(() =>
  import("./appRoutes").then((mod) => ({ default: mod.AuthenticatedAppRouter })),
);
const AccessPage = lazy(() =>
  import("../features/landing/AccessPage").then((m) => ({ default: m.AccessPage })),
);
const LoginPage = lazy(() =>
  import("../features/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("../features/auth/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })),
);
const UpdatePasswordPage = lazy(() =>
  import("../features/auth/UpdatePasswordPage").then((m) => ({ default: m.UpdatePasswordPage })),
);
const SignupPage = lazy(() =>
  import("../features/auth/SignupPage").then((m) => ({ default: m.SignupPage })),
);
const VerifyEmailPage = lazy(() =>
  import("../features/auth/VerifyEmailPage").then((m) => ({ default: m.VerifyEmailPage })),
);
const LegacySignupTypeRedirect = lazy(() =>
  import("../features/auth/SignupPage").then((m) => ({ default: m.LegacySignupTypeRedirect })),
);
const NotFoundPage = lazy(() =>
  import("../marketing/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);
const MarketingLayout = lazy(() =>
  import("../marketing/layouts/MarketingLayout").then((m) => ({ default: m.MarketingLayout })),
);

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner />
    </div>
  );
}

function GoogleOAuthCallbackFallback() {
  const [params] = useSearchParams();
  const next = new URLSearchParams();
  const lang = params.get("lang");
  if (lang) next.set("lang", lang);
  next.set("google_error", "failed");
  return <Navigate to={`/login?${next.toString()}`} replace />;
}

function LegacyOwnerflowRedirect() {
  const location = useLocation();
  const target = location.pathname.replace(/^\/ownerflow\/?/, "/") || "/";
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}

function PublicRouter() {
  return (
    <Routes>
      {MarketingRouteTree()}
      <Route path="/access" element={<AccessPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/update-password" element={<UpdatePasswordPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/:accountType" element={<LegacySignupTypeRedirect />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route
        path="/api/method/frappe.integrations.oauth2_logins.login_via_google"
        element={<GoogleOAuthCallbackFallback />}
      />
      <Route path="/ownerflow/*" element={<LegacyOwnerflowRedirect />} />
      <Route element={<MarketingLayout />}>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export function AppRouter() {
  const { pathname } = useLocation();
  const appPath = isAuthenticatedAppPath(pathname);

  return (
    <Suspense fallback={<RouteFallback />}>
      {appPath ? <AuthenticatedAppRouter /> : <PublicRouter />}
    </Suspense>
  );
}
